import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getApiUrl } from "../services/apiClient";

const UnsubscribeRedirect: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [errorType, setErrorType] = useState<string>("");

  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (!token || token.length !== 64) {
        setStatus("error");
        setErrorType("invalid-token");
        // Redirect to unsubscribed page after 3 seconds
        setTimeout(() => {
          navigate("/unsubscribed?error=invalid-token");
        }, 3000);
        return;
      }

      try {
        const response = await fetch(
          `${getApiUrl()}/grants/notifications/unsubscribe/${token}`,
          {
            method: "GET",
            redirect: "manual", // Don't follow redirects automatically
          }
        );

        if (response.ok || response.status === 0) {
          // Success - the backend would normally redirect, but we're handling it here
          setStatus("success");
          // Redirect to success page after 3 seconds
          setTimeout(() => {
            navigate("/unsubscribed?success=true");
          }, 3000);
        } else {
          setStatus("error");
          setErrorType("token-expired");
          // Redirect to error page after 3 seconds
          setTimeout(() => {
            navigate("/unsubscribed?error=token-expired");
          }, 3000);
        }
      } catch (error) {
        console.error("Unsubscribe error:", error);
        setStatus("error");
        setErrorType("unknown");
        // Redirect to error page after 3 seconds
        setTimeout(() => {
          navigate("/unsubscribed?error=unknown");
        }, 3000);
      }
    };

    handleUnsubscribe();
  }, [token, navigate]);

  const renderContent = () => {
    switch (status) {
      case "success":
        return (
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
            <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-success-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-4">
              Pomyślnie wypisano z powiadomień
            </h1>
            <p className="text-neutral-600 mb-4">
              Nie będziesz już otrzymywać powiadomień email o nowych dotacjach z
              KFS i BUR.
            </p>
            <p className="text-sm text-neutral-500">
              Za chwilę zostaniesz przekierowany na stronę potwierdzenia...
            </p>
          </div>
        );

      case "error":
        const getErrorMessage = (errorType: string) => {
          switch (errorType) {
            case "invalid-token":
              return "Link wypisywania się jest nieprawidłowy. Sprawdź czy skopiowałeś cały link z emaila.";
            case "token-expired":
              return "Link wypisywania się wygasł lub został już użyty. Możesz zmienić ustawienia powiadomień w panelu konta.";
            default:
              return "Wystąpił nieoczekiwany błąd podczas wypisywania się z powiadomień.";
          }
        };

        return (
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-error-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-4">
              Wystąpił problem
            </h1>
            <p className="text-neutral-600 mb-4">
              {getErrorMessage(errorType)}
            </p>
            <p className="text-sm text-neutral-500">
              Za chwilę zostaniesz przekierowany na stronę z dalszymi opcjami...
            </p>
          </div>
        );

      default:
        return (
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-6"></div>
            <h1 className="text-xl font-medium text-neutral-900">
              Przetwarzanie...
            </h1>
            <p className="text-neutral-600 mt-2">
              Przetwarzamy Twoją prośbę o wypisanie się z powiadomień.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      {renderContent()}
    </div>
  );
};

export default UnsubscribeRedirect;
